import {
  AppShell, Burger, Group, Text, ScrollArea, NavLink, Menu, Avatar, UnstyledButton,
  Box, ActionIcon, Tooltip, useMantineColorScheme, useComputedColorScheme, Kbd, Stack, Badge,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { spotlight } from '@mantine/spotlight';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  IconSearch, IconSun, IconMoon, IconLogout, IconSettings, IconChevronDown, IconSchool, IconCheck,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { PRODUCT_NAME } from '../lib/brand';
import api, { fileUrl } from '../services/api';
import { NAV_GROUPS } from '../lib/nav';
import { canAccess, isPlatformSuperadmin } from '../lib/permissions';
import { SILENT_ERROR } from '../lib/queryMeta';
import { SpotlightSearch } from './SpotlightSearch';
import { LanguageMenu, LANGS } from './LanguageMenu';
import { setLanguage } from '../i18n';
import { HeaderBell } from './HeaderBell';
import { HeaderRefresh } from './HeaderRefresh';
import { PoweredBy } from './PoweredBy';

export default function AppLayout() {
  const { t, i18n } = useTranslation();
  const [opened, { toggle, close }] = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { setColorScheme } = useMantineColorScheme();
  const computed = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const dark = computed === 'dark';

  // Superadmin: tasdiq kutayotgan panel qurilmalari soni — menyudagi "Qurilmalar" yonida (spec §8.6).
  // Sanoq endpointi — har daqiqada to'liq ro'yxat (500 qatorgacha) yuklanmaydi (M11)
  const { data: kutilmoqda = 0 } = useQuery({
    queryKey: ['sa-qurilma-kutilmoqda'],
    enabled: isPlatformSuperadmin(user),
    refetchInterval: 60_000,
    queryFn: async () => (await api.get<{ kutilmoqda: number }>('/superadmin/qurilmalar/soni')).data.kutilmoqda,
  });

  // Qabul / CRM: "Ochiq" murojaatlar soni — yangi o'quvchi = maktab uchun yangi mijoz, e'tibor tortsin.
  // Kalit `['admissions', ...]` ostida: Admissions/Students sahifalari har o'zgarishda `['admissions']` ni
  // invalidate qiladi — nishon darhol yangilanadi. `user.id` — boshqa hisobga kirilganda eski son ko'rinmasin.
  // Platforma superadminida maktab yo'q. Fon sanog'i xatosi toast chiqarmaydi (SILENT_ERROR).
  const { data: ochiqMurojaat = 0 } = useQuery({
    queryKey: ['admissions', 'ochiq-soni', user?.id],
    enabled: canAccess(user?.role, 'admissions') && !isPlatformSuperadmin(user),
    refetchInterval: 60_000,
    meta: SILENT_ERROR,
    queryFn: async () => (await api.get<{ ochiq: number }>('/admissions/soni')).data.ochiq,
  });

  /** Menyu bandi yonidagi son. Aktiv (to'q fonli) bandda oq fonli variant — son o'qilsin.
   *  Qizil `red.8`: oq bilan kontrast ~4.5:1 (sukut `red` — 6/5-tus — ~3.3:1, kichik raqam uchun xira). */
  const nishon = (son: number, color: string, aktiv: boolean) =>
    son > 0 ? (
      <Badge size="sm" color={color} variant={aktiv ? 'white' : 'filled'} circle={son <= 99}>
        {son > 99 ? '99+' : son}
      </Badge>
    ) : undefined;
  const ongNishon = (to: string, aktiv: boolean) =>
    to === '/qurilmalar' && kutilmoqda > 0
      ? <Badge size="sm" color="yellow" circle>{kutilmoqda}</Badge>
      : to === '/admissions' ? nishon(ochiqMurojaat, 'red.8', aktiv) : undefined;

  const isActive = (to: string) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  return (
    <AppShell
      header={{ height: 62 }}
      navbar={{ width: 264, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="lg"
    >
      <AppShell.Header withBorder>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Group gap={8} wrap="nowrap">
              <Box
                w={34} h={34}
                style={{ borderRadius: 9, background: 'var(--mantine-color-brand-6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <IconSchool size={20} color="white" />
              </Box>
              {/*
                    MAKTAB nomi, mahsulot nomi EMAS: har tenant panelda o'zini
                    ko'rishi kerak. `tenantName` yo'q bo'lsa (platforma
                    superadmini — uning tenanti yo'q) mahsulot nomi qoladi.
                  */}
                  <Text fw={800} fz="lg" visibleFrom="xs" lineClamp={1}>
                    {user?.tenantName || PRODUCT_NAME}
                  </Text>
            </Group>
          </Group>

          {/* Qidiruv trigger (Spotlight) */}
          <UnstyledButton
            onClick={spotlight.open}
            visibleFrom="sm"
            style={{
              flex: 1, maxWidth: 420, marginInline: 'auto',
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 9999,
              padding: '8px 16px', color: 'var(--mantine-color-dimmed)',
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'var(--mantine-color-body)',
            }}
          >
            <IconSearch size={16} />
            <Text size="sm" c="dimmed" style={{ flex: 1 }}>{t('header.search')}</Text>
            <Kbd size="xs">Ctrl K</Kbd>
          </UnstyledButton>

          <Group gap="xs" wrap="nowrap">
            <HeaderRefresh />
            <HeaderBell />
            {/* TELEFONDA til va tun rejimi profil menyusiga o'tadi: header'ga hammasi
                sig'mas edi (Yangilash qo'shilgach qidiruv va profil ekrandan chiqib ketardi). */}
            <Box visibleFrom="sm"><LanguageMenu /></Box>
            <Tooltip label={dark ? t('header.light') : t('header.dark')}>
              <ActionIcon variant="default" size="lg" visibleFrom="sm" onClick={() => setColorScheme(dark ? 'light' : 'dark')} aria-label="theme">
                {dark ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
            </Tooltip>
            <ActionIcon variant="default" size="lg" hiddenFrom="sm" onClick={spotlight.open} aria-label={t('layout.search')}>
              <IconSearch size={18} />
            </ActionIcon>

            <Menu shadow="md" width={210} position="bottom-end">
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs" wrap="nowrap">
                    <Avatar src={fileUrl(user?.avatar)} color="brand" radius="xl" size={34}>{user?.fullName?.[0]?.toUpperCase() ?? 'U'}</Avatar>
                    <Box visibleFrom="sm">
                      <Text size="sm" fw={600} lh={1.1}>{user?.fullName}</Text>
                      <Text size="xs" c="dimmed">{t(`roles.${user?.role}`)}</Text>
                    </Box>
                    <IconChevronDown size={15} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{user?.fullName}</Menu.Label>
                <Menu.Item leftSection={<IconSettings size={16} />} onClick={() => navigate('/settings')}>{t('header.settings')}</Menu.Item>
                {/* Faqat telefonda — header'dan bu yerga ko'chgan tugmalar */}
                <Box hiddenFrom="sm">
                  <Menu.Item
                    leftSection={dark ? <IconSun size={16} /> : <IconMoon size={16} />}
                    onClick={() => setColorScheme(dark ? 'light' : 'dark')}
                  >
                    {dark ? t('header.light') : t('header.dark')}
                  </Menu.Item>
                  <Menu.Label>{t('lang.title')}</Menu.Label>
                  {LANGS.map((l) => (
                    <Menu.Item
                      key={l.code}
                      onClick={() => setLanguage(l.code)}
                      rightSection={i18n.language?.slice(0, 2) === l.code ? <IconCheck size={16} /> : undefined}
                    >
                      {l.label}
                    </Menu.Item>
                  ))}
                </Box>
                <Menu.Divider />
                <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={() => { logout(); navigate('/login'); }}>{t('header.logout')}</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm" withBorder>
        {/* Menyu ro'yxati qolgan bo'sh joyni egallaydi, muhr esa doim eng pastda turadi
            (`AppShell.Navbar` — flex ustun; `mih={0}` bo'lmasa ScrollArea qisqarmaydi). */}
        <ScrollArea type="scroll" style={{ flex: 1 }} mih={0}>
          <Stack gap="lg">
            {NAV_GROUPS.map((group) => {
              const items = group.items.filter((i) => canAccess(user?.role, i.section));
              if (!items.length) return null;
              return (
                <div key={group.labelKey}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase" px="xs" mb={6} style={{ letterSpacing: 0.4 }}>
                    {t(group.labelKey)}
                  </Text>
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      component={Link}
                      to={item.to}
                      label={t(item.labelKey)}
                      leftSection={<item.icon size={20} stroke={1.6} />}
                      active={isActive(item.to)}
                      onClick={close}
                      variant="filled"
                      rightSection={ongNishon(item.to, isActive(item.to))}
                      styles={{ root: { borderRadius: 14, marginBottom: 4 } }}
                    />
                  ))}
                </div>
              );
            })}
          </Stack>
        </ScrollArea>

        {/* Brend muhri — karkasda, ya'ni barcha sahifalarda ko'rinadi */}
        <PoweredBy />
      </AppShell.Navbar>

      <AppShell.Main style={{ background: dark ? 'var(--mantine-color-dark-8)' : '#f1f2f8' }}>
        <Box maw={1240} mx="auto" key={location.pathname} className="sata-page">
          <Outlet />
        </Box>
      </AppShell.Main>

      <SpotlightSearch />
    </AppShell>
  );
}
